import { useState, useEffect, useRef } from 'react';
import { axiosGet } from '../../utils/axiosGet';
import { DEFAULT_PAGINATION_LIMIT } from '../../utils/constants';
import { devError } from '../../utils/devLog';

export const usePendingNonAasReferees = (
  usersArray,
  nonAasRefereesArray,
  refereeReports,
  refreshUsersByNonAasReferees,
  refreshRefereeReportsByUserIds,
  addNonAasReferee,
) => {
  const [pendingReferees, setPendingReferees] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) {
      return;
    }
    hasRun.current = true;

    const checkPendingReferees = async () => {
      setIsLoading(true);
      try {
        const response = await axiosGet(
          `non-aas-referees?filters[isActive][$eq]=true&pagination[pageSize]=${DEFAULT_PAGINATION_LIMIT}`,
        );

        if (response?.data?.data) {
          const activeNonAasReferees = response.data.data;
          const pending = [];
          const missingUserIds = [];
          const missingRefereeUserIds = [];
          const userByRefereeId = new Map(
            usersArray
              .filter((u) => u.nonAasRefereeId)
              .map((u) => [u.nonAasRefereeId, u]),
          );

          const refereesMissingUsers = activeNonAasReferees.filter(
            (r) => !userByRefereeId.has(r.id),
          );

          if (refereesMissingUsers.length > 0) {
            const CHUNK = 100;
            for (let i = 0; i < refereesMissingUsers.length; i += CHUNK) {
              const slice = refereesMissingUsers.slice(i, i + CHUNK);
              try {
                const inQuery = slice
                  .map((r, j) => `filters[nonAasRefereeId][$in][${j}]=${r.id}`)
                  .join('&');
                const batchResponse = await axiosGet(
                  `users?${inQuery}&pagination[pageSize]=${DEFAULT_PAGINATION_LIMIT}`,
                );
                if (Array.isArray(batchResponse?.data)) {
                  for (const u of batchResponse.data) {
                    if (u?.nonAasRefereeId)
                      userByRefereeId.set(u.nonAasRefereeId, u);
                  }
                }
              } catch (error) {
                devError(
                  'Error batch-fetching users for non-AAS referees:',
                  error,
                );
              }
            }
          }

          for (const referee of activeNonAasReferees) {
            const existsLocally = nonAasRefereesArray.some(
              (r) => r.id === referee.id,
            );

            if (!existsLocally && typeof addNonAasReferee === 'function') {
              addNonAasReferee(referee);
            }

            const user = userByRefereeId.get(referee.id);
            if (!user) {
              missingUserIds.push(referee.id);
              continue;
            }

            if (user.blocked === true) {
              const hasRefereeReport = refereeReports.some(
                (report) => report.attributes.refereeUserId === user.id,
              );
              if (!hasRefereeReport) missingRefereeUserIds.push(user.id);
              pending.push(referee);
            }
          }

          if (
            missingUserIds.length > 0 &&
            typeof refreshUsersByNonAasReferees === 'function'
          ) {
            await refreshUsersByNonAasReferees(missingUserIds);
          }

          if (
            missingRefereeUserIds.length > 0 &&
            typeof refreshRefereeReportsByUserIds === 'function'
          ) {
            await refreshRefereeReportsByUserIds(missingRefereeUserIds);
          }

          setPendingReferees(pending);
          setPendingCount(pending.length);
        }
      } catch (error) {
        devError('Error checking pending referees:', error);
        hasRun.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    checkPendingReferees();
  }, [
    usersArray,
    nonAasRefereesArray,
    refereeReports,
    refreshUsersByNonAasReferees,
    refreshRefereeReportsByUserIds,
    addNonAasReferee,
  ]);

  return { pendingReferees, pendingCount, isLoading };
};
